// themePackModel.js

import {
  ORBITAL_DEFAULT_PACK,
  ThemeMode,
  THEME_COLOR_TOKEN_IDS,
  THEME_META_FIELD_IDS,
} from 'context/theme';

export const THEME_PACK_SCHEMA_VERSION = 1;

function cloneJson(value) {
  return JSON.parse(JSON.stringify(value));
}

/**
 * Build mode token values from a source mode object (builtin or pack).
 * Missing tokens fall back to the builtin default for that mode.
 *
 * @param {object} sourceMode
 * @param {string} modeKey
 * @returns {object}
 */
export function buildThemeModeValues(sourceMode, modeKey) {
  const defaults = ORBITAL_DEFAULT_PACK.modes?.[modeKey] || {};
  const source = sourceMode && typeof sourceMode === 'object' ? sourceMode : {};
  const next = {
    key: modeKey,
  };

  THEME_COLOR_TOKEN_IDS.forEach((tokenId) => {
    if (source[tokenId] !== undefined && source[tokenId] !== null && source[tokenId] !== '') {
      next[tokenId] = source[tokenId];
    } else if (defaults[tokenId] !== undefined) {
      next[tokenId] = defaults[tokenId];
    }
  });

  THEME_META_FIELD_IDS.forEach((fieldId) => {
    if (fieldId === 'key') return;
    if (source[fieldId] !== undefined) {
      next[fieldId] = cloneJson(source[fieldId]);
    } else if (defaults[fieldId] !== undefined) {
      next[fieldId] = cloneJson(defaults[fieldId]);
    }
  });

  return next;
}

/**
 * Create a new personal library pack forked from the builtin default.
 *
 * @param {{ key: string, name?: string, userKey: string }} options
 * @returns {object}
 */
export function createThemePackFromBuiltin({ key, name, userKey }) {
  const packKey = String(key || '').trim();
  const ownerKey = String(userKey || '').trim();
  if (!packKey || !ownerKey) {
    throw new Error('key and userKey are required');
  }

  const dark = buildThemeModeValues(ORBITAL_DEFAULT_PACK.modes[ThemeMode.DARK], ThemeMode.DARK);
  const light = buildThemeModeValues(ORBITAL_DEFAULT_PACK.modes[ThemeMode.LIGHT], ThemeMode.LIGHT);

  return {
    key: packKey,
    id: packKey,
    name: String(name || 'Untitled theme').trim() || 'Untitled theme',
    userKey: ownerKey,
    schemaVersion: THEME_PACK_SCHEMA_VERSION,
    sourcePackId: ORBITAL_DEFAULT_PACK.id,
    modes: {
      [ThemeMode.DARK]: dark,
      [ThemeMode.LIGHT]: light,
    },
  };
}

/**
 * Duplicate an existing pack document into a new key/name.
 *
 * @param {object} sourcePack
 * @param {{ key: string, name?: string, userKey: string }} options
 * @returns {object}
 */
export function duplicateThemePack(sourcePack, { key, name, userKey }) {
  const packKey = String(key || '').trim();
  const ownerKey = String(userKey || '').trim();
  if (!packKey || !ownerKey) {
    throw new Error('key and userKey are required');
  }

  const sourceName = String(sourcePack?.name || 'Untitled theme').trim() || 'Untitled theme';
  const dark = buildThemeModeValues(sourcePack?.modes?.[ThemeMode.DARK], ThemeMode.DARK);
  const light = buildThemeModeValues(sourcePack?.modes?.[ThemeMode.LIGHT], ThemeMode.LIGHT);

  return {
    key: packKey,
    id: packKey,
    name: String(name || `${sourceName} copy`).trim() || `${sourceName} copy`,
    userKey: ownerKey,
    schemaVersion: THEME_PACK_SCHEMA_VERSION,
    sourcePackId: String(sourcePack?.key || sourcePack?.id || sourcePack?.sourcePackId || '').trim() || null,
    modes: {
      [ThemeMode.DARK]: dark,
      [ThemeMode.LIGHT]: light,
    },
  };
}

/**
 * Normalize a pack document for resolveTheme / editor draft.
 *
 * @param {object|null} pack
 * @returns {object|null}
 */
export function normalizeThemePack(pack) {
  if (!pack || typeof pack !== 'object') return null;

  const packKey = String(pack.key || pack.id || '').trim();
  if (!packKey) return null;

  const dark = buildThemeModeValues(
    pack.modes?.[ThemeMode.DARK] || ORBITAL_DEFAULT_PACK.modes[ThemeMode.DARK],
    ThemeMode.DARK,
  );
  const light = buildThemeModeValues(
    pack.modes?.[ThemeMode.LIGHT] || ORBITAL_DEFAULT_PACK.modes[ThemeMode.LIGHT],
    ThemeMode.LIGHT,
  );

  return {
    ...pack,
    key: packKey,
    id: packKey,
    name: String(pack.name || 'Untitled theme').trim() || 'Untitled theme',
    schemaVersion: Number(pack.schemaVersion) || THEME_PACK_SCHEMA_VERSION,
    modes: {
      [ThemeMode.DARK]: dark,
      [ThemeMode.LIGHT]: light,
    },
  };
}

/**
 * Shape a pack as a resolveTheme() pack argument.
 *
 * @param {object} pack
 * @returns {object}
 */
export function toResolvableThemePack(pack) {
  const normalized = normalizeThemePack(pack);
  if (!normalized) {
    return ORBITAL_DEFAULT_PACK;
  }

  return {
    id: normalized.key,
    version: normalized.schemaVersion,
    modes: normalized.modes,
  };
}
